import React from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "../components/site/Seo";
import PageHero from "../components/site/PageHero";
import Reveal from "../components/site/Reveal";
import { SectionLabel, ArrowLink, PrimaryButton, Chip } from "../components/site/Bits";
import ClosingCTA from "../components/home/ClosingCTA";
import NotFound from "./NotFound";
import { SERVICE_PAGES, VERTICALS, APPROACH, SEO } from "../lib/content";
import { IMAGES } from "../lib/images";

export default function ServicePage() {
  const { slug } = useParams();
  const page = SERVICE_PAGES[slug];
  const vertical = VERTICALS.find((v) => v.slug === slug);

  if (!page || !vertical) return <NotFound />;

  const seo = SEO[slug] || SEO.home;
  const visuals = IMAGES.services[slug];
  const heroIntro = page.intro[0];
  const introBody = page.intro.filter((line) => line !== heroIntro && line !== vertical.description);
  const isDark = page.accent === "dark";
  const others = VERTICALS.filter((v) => v.slug !== slug);

  return (
    <>
      <Seo title={seo.title} description={seo.description} path={`/services/${slug}`} />

      <PageHero
        eyebrow={page.eyebrow}
        titleLines={[page.title]}
        intro={heroIntro}
        image={visuals.hero}
      >
        <div className="flex flex-wrap gap-3">
          <PrimaryButton to="/contact" variant="ivory" data-testid="service-hero-cta">
            Start a Conversation
          </PrimaryButton>
          <PrimaryButton to="/work" variant="on-dark">
            See Our Work
          </PrimaryButton>
        </div>
      </PageHero>

      {/* INTRODUCTION */}
      <section className="lk-section" data-testid="service-intro-section">
        <div className="lk-container-wide">
          <div className="grid gap-y-12 lg:grid-cols-12 lg:gap-x-14">
            <div className="lg:col-span-5">
              <Reveal>
                <SectionLabel>Introduction</SectionLabel>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="editorial-sm mt-5 max-w-[22ch]">{page.tagline}</h2>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <div className="space-y-6">
                {introBody.map((paragraph, i) => (
                  <Reveal key={paragraph} delay={0.1 + i * 0.06}>
                    <p className="lead measure">{paragraph}</p>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.26}>
                <div className="mt-9 flex flex-wrap gap-[10px]">
                  {vertical.services.map((service) => (
                    <Chip key={service}>{service}</Chip>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section
        className={isDark ? "cinematic relative overflow-hidden" : ""}
        style={isDark ? undefined : { background: "var(--lykit-lavender)" }}
        data-testid="service-capabilities-section"
      >
        <div className="lk-container-wide py-[clamp(38px,4vw,66px)]">
          <Reveal>
            <SectionLabel onDark={isDark}>{page.capabilitiesLabel}</SectionLabel>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="display-lg mt-5 max-w-[24ch]" style={isDark ? { color: "#FBFAF7" } : undefined}>
              {page.capabilitiesLabel}.
            </h2>
          </Reveal>

          <div className="mt-[clamp(36px,4vw,64px)] grid sm:grid-cols-2 lg:grid-cols-3">
            {page.capabilities.map((cap, i) => (
              <Reveal
                key={cap.title}
                delay={i * 0.04}
                className="py-8 sm:px-8 sm:first:pl-0"
                style={{
                  borderTop: `1px solid ${isDark ? "rgba(247,246,242,0.16)" : "rgba(23,21,34,0.12)"}`,
                }}
              >
                <span
                  className="font-display text-[11.5px] font-bold"
                  style={{
                    letterSpacing: "0.16em",
                    color: isDark ? "rgba(216,183,122,0.85)" : "rgba(23,21,34,0.38)",
                  }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3
                  className="mt-4 font-display text-[clamp(17px,1.4vw,21px)] font-extrabold tracking-[-0.02em]"
                  style={isDark ? { color: "#FBFAF7" } : undefined}
                >
                  {cap.title}
                </h3>
                <p
                  className="mt-3 max-w-[34ch] text-[14px] leading-[1.62]"
                  style={{ color: isDark ? "rgba(247,246,242,0.72)" : "rgba(23,21,34,0.72)" }}
                >
                  {cap.text}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VISUALS */}
      <section className="lk-section" data-testid="service-visuals-section">
        <div className="lk-container-wide">
          <div className="grid gap-5 lg:h-[clamp(360px,52vh,540px)] lg:grid-cols-12">
            <Reveal variant="image" className="h-full lg:col-span-7">
              <div className="img-frame img-zoom h-full min-h-[240px] w-full">
                <img src={visuals.gallery[0]} alt={`${page.title} work`} loading="lazy" />
              </div>
            </Reveal>
            <div
              className="grid h-full gap-5 lg:col-span-5"
              style={{
                gridTemplateRows: `repeat(${Math.max(visuals.gallery.length - 1, 1)}, minmax(0, 1fr))`,
              }}
            >
              {visuals.gallery.slice(1).map((src, i) => (
                <Reveal variant="image" key={src} delay={0.08 + i * 0.06} className="h-full">
                  <div className="img-frame img-zoom h-full min-h-[180px] w-full">
                    <img src={src} alt={`${page.title} work`} loading="lazy" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="lk-section-tight" data-testid="service-process-section">
        <div className="lk-container-wide">
          <div className="grid gap-y-10 lg:grid-cols-12 lg:gap-x-14">
            <div className="lg:col-span-4">
              <Reveal>
                <SectionLabel>How We Work</SectionLabel>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="display-md mt-7 max-w-[14ch]">FROM IDEA TO IMPACT.</h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {APPROACH.map((step, i) => (
                  <Reveal
                    as="li"
                    key={step.num}
                    delay={i * 0.05}
                    className="flex flex-col gap-2 py-6 sm:flex-row sm:gap-10"
                    style={{ borderTop: "1px solid rgba(23,21,34,0.10)" }}
                  >
                    <span
                      className="shrink-0 font-display text-[12px] font-bold"
                      style={{ letterSpacing: "0.16em", color: "rgba(23,21,34,0.4)", paddingTop: 5, width: 40 }}
                    >
                      {step.num}
                    </span>
                    <div>
                      <h3 className="font-display text-[18px] font-extrabold tracking-[-0.02em]">{step.title}</h3>
                      <p className="mt-2 max-w-[46ch] text-[14px] leading-[1.62] body-muted">{step.text}</p>
                    </div>
                  </Reveal>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* OTHER VERTICALS */}
      <section className="lk-section-tight" data-testid="service-other-verticals">
        <div className="lk-container-wide">
          <Reveal>
            <SectionLabel>More From LYKIT</SectionLabel>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2">
            {others.map((other) => (
              <Link
                key={other.slug}
                to={other.to}
                data-testid={`service-cross-link-${other.slug}`}
                className="group flex items-center justify-between px-1 py-6 transition-colors duration-300 sm:px-6 sm:first:pl-1"
                style={{ borderTop: "1px solid rgba(23,21,34,0.12)" }}
              >
                <span>
                  <span
                    className="block font-display text-[11.5px] font-bold"
                    style={{ letterSpacing: "0.16em", color: "rgba(23,21,34,0.38)" }}
                  >
                    {other.num}
                  </span>
                  <span className="mt-2 block font-display text-[clamp(16px,1.4vw,20px)] font-extrabold tracking-[-0.02em]">
                    {other.title}
                  </span>
                </span>
                <span
                  aria-hidden="true"
                  className="text-[18px] transition-transform duration-300 group-hover:translate-x-1"
                  style={{ color: "#352A59" }}
                >
                  &rarr;
                </span>
              </Link>
            ))}
          </div>
          <div className="mt-10">
            <ArrowLink to="/contact">Talk to us about {vertical.title.toLowerCase()}</ArrowLink>
          </div>
        </div>
      </section>

      <ClosingCTA />
    </>
  );
}
